import { useCallback, useEffect } from "react";
import useSWR from "swr";
import { useAuth } from "@/lib/config/auth-provider";
import { quickStudiesApi } from "@/lib/api/quick-study-native";
import { reportStudyCardsLoadFailure } from "@/lib/utils/report-study-cards-load-failure";
export function useThreadQuickStudies(
  threadToken?: string,
  observingTurn = false,
) {
  const { isAuthenticated, user } = useAuth();
  const enabled = isAuthenticated && Boolean(user?.id) && Boolean(threadToken);
  const { data, error, isLoading, mutate } = useSWR(
    enabled ? ["thread-native-quick-studies", user!.id, threadToken!] : null,
    () => quickStudiesApi.list(threadToken!),
    {
      refreshInterval: (rows) =>
        observingTurn ||
        rows?.some(
          (draft) =>
            draft.activeRun?.status === "pending" ||
            draft.activeRun?.status === "running",
        )
          ? 3_000
          : 0,
      dedupingInterval: 1_000,
      refreshWhenHidden: false,
      refreshWhenOffline: false,
      onError: (failure) =>
        reportStudyCardsLoadFailure("quick_studies", threadToken!, failure),
    },
  );
  const refresh = useCallback(() => mutate(undefined, { revalidate: true }), [mutate]);
  useEffect(() => {
    if (enabled && observingTurn) void refresh();
  }, [enabled, observingTurn, refresh]);
  return {
    items: enabled ? (data ?? []) : [],
    isLoading: enabled && isLoading,
    error,
    refresh,
  };
}

/** An explicit run link reads its pinned revision, never the newest authoring selection. */
export function useThreadQuickStudyState(
  threadToken?: string,
  draftToken?: string,
  runToken?: string,
  revision?: number,
) {
  const { isAuthenticated, user } = useAuth();
  const enabled =
    isAuthenticated &&
    Boolean(user?.id) &&
    Boolean(threadToken) &&
    Boolean(draftToken);
  const { data, error, isLoading, mutate } = useSWR(
    enabled
      ? [
          "thread-native-quick-state",
          user!.id,
          threadToken!,
          draftToken!,
          runToken ?? null,
          revision ?? null,
        ]
      : null,
    () => quickStudiesApi.read(threadToken!, draftToken!, runToken, revision),
    {
      refreshInterval: (value) =>
        value?.activeRun?.status === "pending" ||
        value?.activeRun?.status === "running"
          ? 3_000
          : 0,
      refreshWhenHidden: false,
    },
  );
  return {
    data: enabled ? data : undefined,
    error,
    isLoading: enabled && isLoading,
    refresh: mutate,
  };
}
